// Regras da linha da peça na Ficha de OS (#85), sem DOM: o que cada campo
// mostra, divergência, o que desenhar da Prévia do Fechamento e o selo da
// categoria. `_ficha-tipo-peca.ts` é o adapter: lê os campos, chama daqui e
// desenha. Nada do Fechamento é calculado aqui — vem pronto na Prévia (#84).

import type { PreviaTipo, RecusaFechamento } from "../../domain/devolucao.ts";
import type { SaldoLinha } from "../../domain/backlog.ts";
import type { Trecho } from "../../domain/situacao-retencao.ts";
import type { Impedimento } from "./_resumo-categoria.ts";

/** O que está digitado na linha agora. */
export interface EstadoLinha {
  recebida: number;
  /** Texto do campo Conferida ("" = vazio). */
  conferida: string;
  conferidaTravada: boolean;
  /** Há divergência gravada: a Conferida não acompanha a Recebida. */
  conferidaEraDivergente: boolean;
  motivoDivergencia: string;
  devolvida: number;
  retidas: readonly { motivo: string; quantidade: number }[];
  /** Retida digitada na Coleta. */
  retidaInformada: number | null;
  fechamentoAberto: boolean;
}

/** Quantidade para um campo: zero fica vazio (o "0" é a dica). */
export function semZero(n: number): string {
  return n > 0 ? String(n) : "";
}

/**
 * O campo Retida mostra sempre a Retida digitada da Comanda — o Backlog do
 * hotel daquela peça, que o "Fechar OS" confere (ADR-0029) —, com a OS
 * aberta ou fechada; editável enquanto a Coleta está aberta, mesmo em peça
 * sem Recebida nem Devolvida. As retidas por motivo ficam no detalhe da peça
 * (decisão do dono em 2026-10-02; antes, OS fechada mostrava a soma delas).
 */
export function campoRetida(e: EstadoLinha): string {
  return semZero(e.retidaInformada ?? 0);
}

/**
 * Enquanto a Conferida está travada (Coleta) e não há divergência gravada,
 * ela acompanha a Recebida — sem isso a linha nova "divergia" contra o "0".
 */
export function campoConferida(e: EstadoLinha): string {
  return e.conferidaTravada && !e.conferidaEraDivergente ? semZero(e.recebida) : e.conferida;
}

/** Conferida diferente da Recebida; pendente enquanto falta o motivo. */
export function divergencia(e: EstadoLinha): { divergente: boolean; pendente: boolean } {
  const txt = e.conferida.trim();
  const divergente = txt !== "" && Number(txt) !== e.recebida;
  return { divergente, pendente: divergente && e.motivoDivergencia.trim() === "" };
}

export interface LinhaSituacao {
  tom: "alerta" | "ok";
  trechos: Trecho[];
}

export interface DesenhoPrevia {
  /** "Backlog do hotel: Backlog (3) + retida na OS (2) = 5"; `null` = não mostra. */
  conta: Trecho[] | null;
  /** De onde sai a carona (para "↳ do Backlog: …"). */
  origem: readonly SaldoLinha[];
  /** As recusas da peça na ordem do "Fechar OS"; sem recusa, "Tudo confere". */
  situacao: LinhaSituacao[];
  /** Há recusa: o detalhe da peça abre e o botão de detalhe fica em alerta. */
  alerta: boolean;
}

/**
 * O que desenhar da Prévia nesta peça. `null` = nada: Fechamento travado,
 * sem Prévia (OS nova) ou nada digitado e nenhuma recusa.
 */
export function desenhoDaPrevia(
  e: EstadoLinha,
  previa: PreviaTipo | null,
  recusas: readonly RecusaFechamento[],
): DesenhoPrevia | null {
  if (!previa || !e.fechamentoAberto) return null;
  const digitou = e.devolvida > 0 || e.retidas.some((r) => r.quantidade > 0);
  if (!digitou && recusas.length === 0) return null;
  const r = previa.retencao;
  // nada a dizer: sem Backlog, sem retenção e sem Retida digitada
  const semAssunto = !r || (r.backlogAntes === 0 && r.retidaNaOS === 0 && !e.retidaInformada);
  // carona acima do Backlog: a conta do hotel não fecha — o aviso dela manda
  const semSaldo = recusas.some((x) => x.natureza === "carona");

  const situacao: LinhaSituacao[] = [];
  // a retenção é um aviso só, mesmo com várias recusas dela (ex.: motivo em
  // branco + "Retidas sem motivo"); sem impedimento na conta, vale o texto da recusa
  let retencaoMostrada = false;
  for (const recusa of recusas) {
    if (recusa.natureza === "carona") {
      for (const linha of previa.carona?.aviso ?? []) situacao.push({ tom: "alerta", trechos: linha });
    } else if (recusa.natureza === "retencao") {
      if (semSaldo) continue;
      if (r?.impedimento) {
        if (!retencaoMostrada) situacao.push({ tom: "alerta", trechos: r.situacao });
        retencaoMostrada = true;
      } else {
        situacao.push({ tom: "alerta", trechos: [recusa.erro.message] });
      }
    } else {
      situacao.push({ tom: "alerta", trechos: [recusa.erro.message] });
    }
  }
  if (recusas.length === 0 && r && !semAssunto) situacao.push({ tom: "ok", trechos: r.situacao });

  return {
    conta: r && !semSaldo && !semAssunto && r.impedimento !== "semDevolvida" ? r.conta : null,
    origem: previa.carona?.origem ?? [],
    situacao,
    alerta: recusas.length > 0,
  };
}

/**
 * O selo da categoria para esta peça: o motivo da divergência que falta,
 * senão a primeira recusa da Prévia no vocabulário do resumo. Peça sem
 * Devolvida não acende — a categoria já diz "A Fechar".
 */
export function impedimentoDaLinha(e: {
  pendente: boolean;
  fechamentoAberto: boolean;
  previa: PreviaTipo | null;
  recusas: readonly RecusaFechamento[];
}): Impedimento | null {
  if (e.pendente) return "motivo";
  if (!e.fechamentoAberto) return null;
  const primeira = e.recusas[0];
  if (!primeira) return null;
  if (primeira.natureza === "carona") return "backlog";
  if (primeira.natureza === "retencao") {
    // recusa da retenção sem impedimento na conta = motivo em branco
    const i = e.previa?.retencao?.impedimento ?? "semMotivo";
    return i === "semDevolvida" ? null : i;
  }
  return "outro";
}

/** Sugestão do "+ Motivo de Retenção": o máximo que ainda cabe; sem Prévia, 1. */
export function sugestaoMotivo(previa: PreviaTipo | null): number {
  return previa?.retencao?.sugestao ?? 1;
}
